// Shared view model and small marks for the work views (board, list, table, calendar, timeline,
// gallery). Tasks are view models: the app maps its rows onto `TaskSummary`; nothing here fetches
// or computes "overdue" from a clock (the caller says so).
import {
  CheckSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleDotIcon,
  CircleIcon,
  EqualIcon,
  ListTreeIcon,
  MessageSquareIcon,
  PaperclipIcon,
  TriangleAlertIcon,
} from "lucide-react";
import type * as React from "react";

import { cn } from "../../cn";
import { Badge } from "../badge";
import { TagChip, type TagChipColor } from "../tag-chip";

/** One of a task's labels: a company tag as its coloured chip, else a toned badge. */
export function TaskLabel({ label }: { label: NonNullable<TaskSummary["labels"]>[number] }) {
  return label.tag ? (
    <TagChip name={label.label} color={label.tag} />
  ) : (
    <Badge variant={label.tone ?? "neutral"}>{label.label}</Badge>
  );
}

export type StatusTone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

export type TaskPriority = "urgent" | "high" | "medium" | "low";

export type TaskPerson = {
  name: string;
  kind?: "person" | "agent";
  src?: string;
  /** Short text beside the tile in a row ("Dana W."); the name when absent. */
  label?: string;
  /** The tooltip ("Dana Whitfield · Recruiting lead"); the name when absent. */
  title?: string;
};

export type TaskSummary = {
  id: string;
  title: string;
  status?: { label: string; tone?: StatusTone; kind?: "todo" | "active" | "done" | "blocked" };
  done?: boolean;
  assignees?: TaskPerson[];
  /** ISO date (YYYY-MM-DD). */
  start?: string;
  due?: string;
  overdue?: boolean;
  priority?: TaskPriority;
  /** WOS-BUNDLE: a label with `tag` is a company tag, drawn as its coloured chip. */
  labels?: { label: string; tone?: StatusTone; tag?: TagChipColor }[];
  counts?: {
    subtasks?: { done: number; total: number };
    /** WORK-OS: the checklist items apart from the subtasks (ClickUp's two counters). */
    checklist?: { done: number; total: number };
    comments?: number;
    files?: number;
  };
  /** TASK-FILES: cover image URLs (board cards show up to four). */
  covers?: string[];
  /** "Ops › Hiring", shown on inline cards. */
  location?: string;
  href?: string;
  /**
   * TASK-LOCK: an agent is working on it (who, and since when as a short label): the card is
   * greyed, carries an "AG working" badge and cannot be dragged or moved by keyboard.
   */
  locked?: { by: string; since: string };
};

/** A person or agent as an `AvatarStack` item (no `src` key when there is no photo). */
export function toAvatar(p: {
  name: string;
  kind?: "person" | "agent" | undefined;
  src?: string | undefined;
}) {
  return {
    name: p.name,
    kind: p.kind === "agent" ? ("agent" as const) : ("person" as const),
    ...(p.src ? { src: p.src } : {}),
  };
}

export function StatusBadge({ status }: { status: NonNullable<TaskSummary["status"]> }) {
  return (
    <Badge variant={status.tone ?? "neutral"} dot>
      {status.label}
    </Badge>
  );
}

const STATUS_GLYPH = {
  todo: CircleIcon,
  active: CircleDotIcon,
  done: CircleCheckIcon,
  blocked: CircleDashedIcon,
} as const;

/** 14 px status glyph for row leads (Work OS StatusGlyph). */
export function StatusGlyph({ task, className }: { task: TaskSummary; className?: string }) {
  const kind = task.done ? "done" : (task.status?.kind ?? "todo");
  const Icon = STATUS_GLYPH[kind];
  return (
    <Icon
      data-slot="status-glyph"
      data-kind={kind}
      aria-hidden={true}
      className={cn(
        "size-3.5",
        kind === "done" ? "text-success" : kind === "active" ? "text-primary" : "text-faint",
        className,
      )}
    />
  );
}

const PRIORITY: Record<TaskPriority, { icon: typeof ChevronUpIcon; label: string; cls: string }> = {
  urgent: { icon: TriangleAlertIcon, label: "Urgent", cls: "text-danger-text" },
  high: { icon: ChevronUpIcon, label: "High", cls: "text-warning-text" },
  medium: { icon: EqualIcon, label: "Medium", cls: "text-secondary" },
  low: { icon: ChevronDownIcon, label: "Low", cls: "text-muted-foreground" },
};

/** Icon + an accessible "Priority: High"; the word shows only with `showLabel`. */
export function PriorityMark({
  priority,
  showLabel = false,
}: {
  priority: TaskPriority;
  showLabel?: boolean;
}) {
  const p = PRIORITY[priority];
  return (
    <span
      data-slot="priority-mark"
      data-priority={priority}
      className={cn("inline-flex items-center gap-1", p.cls)}
    >
      <p.icon aria-hidden={true} className="size-3.5" />
      {showLabel ? p.label : <span className="sr-only">Priority: {p.label}</span>}
    </span>
  );
}

/** "19 Oct" (local), in --danger-text when the caller says it is overdue. */
export function DueLabel({ due, overdue = false }: { due: string; overdue?: boolean | undefined }) {
  return (
    <time
      data-slot="due-label"
      dateTime={due}
      className={cn("tabular-nums", overdue && "font-medium text-danger-text")}
    >
      {shortDate(due)}
      {overdue ? <span className="sr-only"> (overdue)</span> : null}
    </time>
  );
}

/** Parses "YYYY-MM-DD" as a local calendar date (never shifted by the time zone). */
export function parseDay(day: string): Date {
  const [y, m, d] = day.slice(0, 10).split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

export function shortDate(day: string, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(parseDay(day));
}

/** The comments / files / subtasks counts as `MetaCounts` items. */
export function countItems(task: TaskSummary) {
  const items: {
    icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
    count: number;
    label: string;
    /** Shown instead of the bare count ("2/5"). */
    text?: string;
  }[] = [];
  const c = task.counts;
  if (c?.subtasks && c.subtasks.total > 0)
    items.push({
      icon: ListTreeIcon,
      count: c.subtasks.done,
      label: `of ${c.subtasks.total} subtasks done`,
      text: `${c.subtasks.done}/${c.subtasks.total}`,
    });
  if (c?.checklist && c.checklist.total > 0)
    items.push({
      icon: CheckSquareIcon,
      count: c.checklist.done,
      label: `of ${c.checklist.total} checklist items done`,
      text: `${c.checklist.done}/${c.checklist.total}`,
    });
  if (c?.comments) items.push({ icon: MessageSquareIcon, count: c.comments, label: "comments" });
  if (c?.files) items.push({ icon: PaperclipIcon, count: c.files, label: "files" });
  return items;
}

export const TONE_DOT: Record<StatusTone, string> = {
  neutral: "bg-border-strong",
  accent: "bg-primary",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
  info: "bg-info",
};
